import {
  Container,
  Typography,
  Box,
  LinearProgress,
  Alert,
  AlertTitle,
} from '@mui/material';
import { useParams } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { dictionaryService, Token } from './services/dictionaryService';
import FormPagePerson from './FormPagePerson';
import FormPageB2B from './FormPageB2B';
import LanguageSwitch from './componets/LanguageSwitch';
import { useLanguage } from './i18n/LanguageContext';
import { TranslationKey } from './i18n/translations';

export interface ContactDetails {
  firstName: string;
  lastName: string;
  email: string;
  phoneNo: string;
}

const CONTACT_MAIL = 'pomoc@itaxlabs.com';

const FormPage = () => {
  const { token } = useParams();
  const { t } = useLanguage();
  const [loading, setLoading] = useState(true);
  const [errorType, setErrorType] = useState<string | null>(null);
  const [tokenAll, setTokenAll] = useState<Token | null>(null);
  const [contactDetails, setContactDetails] = useState<ContactDetails | null>(null);
  const [formType, setFormType] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      if (!token) {
        setLoading(false);
        setErrorType('missing');
        return;
      }

      try {
        const data = await dictionaryService.getFormByToken(token);
        setTokenAll(data.token);
        setFormType(data.formType);
        setContactDetails(data.contactDetails);
      } catch (error: unknown) {
        let serverError;
        type ErrorResponse = {
          response?: {
            data?: {
              error?: string;
            };
          };
        };
        if (
          typeof error === 'object' &&
          error !== null &&
          'response' in error &&
          typeof (error as ErrorResponse).response === 'object' &&
          (error as ErrorResponse).response !== undefined &&
          'data' in (error as ErrorResponse).response! &&
          typeof (error as ErrorResponse).response!.data === 'object' &&
          (error as ErrorResponse).response!.data !== undefined &&
          'error' in (error as ErrorResponse).response!.data!
        ) {
          serverError = (error as ErrorResponse).response!.data!.error;
        }

        if (serverError === 'form_outdated') {
          setErrorType('expired');
        } else if (serverError === 'incorrect_token') {
          setErrorType('incorrect');
        } else if (serverError === 'form_already_submitted') {
          setErrorType('used');
        } else {
          setErrorType('server'); // fallback na brak konkretnego błędu
        }
      }
      finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [token]);

  const mailLink = (
    <strong style={{ marginInline: '5px' }}>
      <a href={`mailto:${CONTACT_MAIL}`} style={{ color: 'inherit', textDecoration: 'underline' }}>
        {CONTACT_MAIL}
      </a>
    </strong>
  );

  // Każdy błąd z kontaktem ma ten sam układ: treść, link mailowy, dokończenie zdania.
  const contactAlert = (severity: 'warning' | 'error', prefix: string) => (
    <Alert severity={severity} sx={{ mt: 4 }}>
      <AlertTitle>{t(`${prefix}.title` as TranslationKey)}</AlertTitle>
      {t(`${prefix}.body` as TranslationKey)}
      {mailLink}
      {t(`${prefix}.after` as TranslationKey)}
    </Alert>
  );

  const renderError = () => {
    switch (errorType) {
      case 'expired':
        return contactAlert('warning', 'error.expired');
      case 'incorrect':
        return contactAlert('error', 'error.incorrect');
      case 'used':
        return contactAlert('error', 'error.used');
      case 'server':
        return contactAlert('error', 'error.server');
      case 'missing':
        return (
          <Alert severity="error" sx={{ mt: 4 }}>
            <AlertTitle>{t('error.missing.title')}</AlertTitle>
            {t('error.missing.body')}
          </Alert>
        );
      default:
        return null;
    }
  };

  return (
    <>
      <Box
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          zIndex: 1100,
          height: '84px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderBottom: '1px solid #00D2B4',
          padding: '0 16px',
          // MysticLogo.png jest białe na przezroczystym tle - na białym pasku byłoby
          // niewidoczne, dlatego nagłówek dostaje ciemny podkład.
          backgroundColor: '#1A202C',
        }}
      >
        <Typography
          variant="h6"
          component="div"
          sx={{ display: 'flex', alignItems: 'center', marginLeft: { xs: '1vw', md: '20px' } }}
        >
          <img
            src={`${process.env.PUBLIC_URL}/MysticLogo.png`}
            alt="Mystic Creatures Festival"
            style={{ height: '64px', width: 'auto', display: 'block' }}
          />
        </Typography>

        {loading && (
          <LinearProgress
            sx={{
              height: 3,
              width: '100%',
              position: 'absolute',
              bottom: 0,
              left: 0,
              backgroundColor: '#00D2B4',
              '& .MuiLinearProgress-bar': {
                backgroundColor: '#B2DFDB',
              },
            }}
          />
        )}
      </Box>

      <Container maxWidth="lg">
        {/* Pod stałym nagłówkiem (84px), zawsze widoczny — także na ekranach ładowania i błędów */}
        <Box sx={{ marginTop: '108px' }}>
          <LanguageSwitch />
        </Box>
        {loading ? (
          <Alert severity="info" sx={{ mt: 4 }}>
            <AlertTitle>{t('loading.title')}</AlertTitle>
            {t('loading.body')}
          </Alert>
        ) : errorType ? (
          renderError()
        ) : contactDetails !== undefined && contactDetails !== null && tokenAll ? (
          <>
            {formType === 'PERSONAL' ? (
              <FormPagePerson tokenAll={tokenAll} contactDetails={contactDetails} />
            ) : (
              <FormPageB2B tokenAll={tokenAll} contactDetails={contactDetails} />
            )}
          </>
        ) : null}
      </Container>
    </>
  );
};

export default FormPage;
